import { EntitiesHallAdditionalDataModel } from '@cw-elements/mfe-setup/entities-hall';
import { ROUTES } from 'common/constants';
import { FarmIcon } from 'common/icons';
import {
  setLocalData,
  useNavigateWithOrgAndFarm,
  useTracking,
} from 'common/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'src/auth';
import { CropwiseAiTabsEnum } from 'src/pages/cropwise-ai';
import { useFarm, useOrganization } from 'src/pages/organization';
import OnboardingStatusEnum from 'src/pages/organization/onboarding-status.enum';
import { NeutralColors } from 'theme';

const useTopbarNavigation = () => {
  const [showType, setShowType] = useState<CropwiseAiTabsEnum>(
    CropwiseAiTabsEnum.HIDE,
  );
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const { signout } = useAuth();
  const navigate = useNavigateWithOrgAndFarm();
  const location = useLocation();
  const { track } = useTracking();

  useEffect(() => {
    setLocalData(
      'cropwise_experience_closed_switch_info_topbar_navigation',
      true,
    );
  }, []);

  useEffect(() => {
    if (showType !== CropwiseAiTabsEnum.HIDE) {
      setShowType(CropwiseAiTabsEnum.COLLAPSE);
    }
  }, [location.pathname]);

  const {
    currentWorkspaceId,
    currentOrganizationId,
    selectedWorkspaceId,
    selectedOrganizationId,
    organizations,
  } = useOrganization();
  const { currentFarm, setCurrentFarmId } = useFarm();

  // The Settings MFE only renders once it receives an organization id, and the
  // guided onboarding it hosts also needs the workspace id. Both are known as soon
  // as the user picks an entity in My Cropwise, which happens before the
  // organization becomes the product context (and never becomes it while its
  // onboarding is pending). See FMS-2589.
  const topbarWorkspaceId = currentWorkspaceId ?? selectedWorkspaceId;
  const topbarOrganizationId = currentOrganizationId ?? selectedOrganizationId;

  const onClickGoToMyCropwise = useCallback(() => {
    navigate(ROUTES.MY_CROPWISE, null, null);
  }, [navigate]);

  const featureConfig = useMemo(
    () => ({
      hasProfileDropdown: true,
      hasDeckOfApps: true,
      hasSettingsMenu: true,
      hideEntitiesDropdown: location.pathname.startsWith(ROUTES.MY_CROPWISE),
    }),
    [location.pathname],
  );

  const onEntitiesSelect = useCallback(
    (selectedEntities: {
      workspaceId: string | null;
      divisionId: string | null;
      organizationId: string | null;
      farmId: string | null;
    }) => {
      if (!selectedEntities.organizationId || !selectedEntities.farmId) {
        return;
      }
      const selectedOrganization = organizations.find(
        (org) => org.id === selectedEntities.organizationId,
      );
      if (
        selectedOrganization?.onboardingStatus !==
        OnboardingStatusEnum.COMPLETED
      ) {
        navigate(
          `${ROUTES.MY_CROPWISE}/workspace/${selectedEntities.workspaceId}`,
          null,
          null,
        );
        return;
      }

      navigate(
        location.pathname,
        selectedEntities.organizationId,
        selectedEntities.farmId,
      );
    },
    [location.pathname, navigate, organizations],
  );

  const handleOnChangeLanguage = useCallback(
    (language: string) => {
      i18n.changeLanguage(language);
    },
    [i18n],
  );

  const handleLogout = useCallback(() => {
    signout();
  }, [signout]);

  const handleOpenCropwiseAi = useCallback(() => {
    track('Cropwise AI - Opened from Topbar');
    setShowType(CropwiseAiTabsEnum.FULL_SCREEN);
  }, []);

  const handleToggleNotifications = useCallback(() => {
    setIsNotificationsOpen((prev) => !prev);
  }, []);

  const handleCloseNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
  }, []);

  const handleClickToSeeOrgLevel = useCallback(
    (organizationId: string) => {
      navigate(location.pathname, organizationId, null);
      setCurrentFarmId(null);
    },
    [navigate, location.pathname],
  );

  const entitiesSelectorAdditionalData = useCallback(
    (value: string, type: string): EntitiesHallAdditionalDataModel | null => {
      if (type !== 'organization') return null;
      const organization = organizations.find((org) => org.id === value);
      if (!organization) return null;

      if (organization.onboardingStatus !== OnboardingStatusEnum.COMPLETED)
        return null;

      return {
        onClick: () => {
          handleClickToSeeOrgLevel(organization.id);
        },
        data: t('topbar_navigation.view_all_farms'),
        tooltip: t('topbar_navigation.view_all_farms_tooltip'),
        icon: (
          <FarmIcon
            style={{ width: 16, height: 16, fill: NeutralColors[60] }}
          />
        ),
      };
    },
    [organizations, handleClickToSeeOrgLevel],
  );

  return {
    userLanguage: i18n.language,
    handleOnChangeLanguage,
    onClickGoToMyCropwise,
    onEntitiesSelect,
    handleLogout,
    currentOrganizationId,
    currentWorkspaceId,
    topbarOrganizationId,
    topbarWorkspaceId,
    currentFarmExternalId: currentFarm?.externalId || null,
    featureConfig,
    handleOpenCropwiseAi,
    showType,
    setShowType,
    entitiesSelectorAdditionalData,
    isNotificationsOpen,
    handleToggleNotifications,
    handleCloseNotifications,
  };
};

export { useTopbarNavigation };
